import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Grid,
    GridColumn,
    Icon,
    ListItem,
    View,
    colors,
    Link,
    Button,
    Popover,
} from '@fairygodboss/ui';

import { convincerPropType } from './constants';

const iconMap = {
    shield: 'badge_20',
    wing: 'ratingWingOutline_20',
    diversity_performance: 'ribbon_20',
    ribbon: 'ribbon_20',
    thumbs_up: 'thumbsUpConviser_20',
    trophy_icon: 'trophy_icon_20',
    megaphone: 'megaphone_20',
    scales: 'scales_20',
    giving_hand: 'giving_hand_20',
    diversity: 'diversity_20',
};

const ListItemSVG = ({
    svgVariant,
    index,
    text,
    subtext,
    url,
    isLast,
    size,
    getRef = null,
    numberOfLines = 1,
}) => {
    const IconListItem = (
        <View
            width="34px"
            height="34px"
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mr="2px"
        >
            {iconMap[svgVariant] ? (
                <Icon
                    variant={iconMap[svgVariant]}
                    outlineColor={colors.neutral[100]}
                />
            ) : (
                <View fontWeight="700" fontSize="14px" lineHeight="18px">
                    {svgVariant}
                </View>
            )}
        </View>
    );
    let rightMargin = '18px';
    if (index % 2) rightMargin = '0px';
    const css = {
        div: {
            display: 'flex',
        },
        strong: {
            fontWeight: 400,
            lineHeight: '24px',
            fontSize: '14px',
        },
        li: {
            minHeight: '77px',
        },
        b: {
            lineHeight: '24px',
            fontSize: '14px',
            fontWeight: 700,
        },
        a: {
            fontWeight: 600,
            color: colors.primary[60],
            lineHeight: '24px',
            fontSize: '14px',
        },
        'a:visited': {
            color: colors.secondary[70],
            opacity: 1.0,
        },
        'a:hover': {
            color: colors.primary[80],
            textDecoration: 'underline',
            opacity: 1.0,
        },
        'a:visited:hover': {
            color: colors.secondary[70],
            textDecoration: 'underline',
            opacity: 1.0,
        },
        'a:active': {
            color: colors.secondary[80],
            textDecoration: 'underline',
        },
    };
    let topPosistion = '0px';

    if (svgVariant !== 'ribbon') {
        if (numberOfLines === 2) topPosistion = '-12px';

        if (numberOfLines >= 3) topPosistion = '-23px';
    } else {
        css.div = {
            background: 'transparent',
        };
        if (numberOfLines === 2) topPosistion = '-10px';

        if (numberOfLines === 3) topPosistion = '-3px';
    }

    let maxHeight = { _: '66px' };
    let pbValue = { _: '16px' };
    if (svgVariant === 'ribbon') {
        maxHeight = { _: '160px' };
        pbValue = { _: '0px' };
    }

    return (
        <GridColumn
            size={size}
            maxHeight={maxHeight}
            pb={!isLast ? '16px' : undefined}
            mb={numberOfLines > 3 ? '40px' : '5px'}
        >
            <ListItem
                id="test"
                variant="icon"
                color={colors.neutral[100]}
                icon={IconListItem}
                pb={pbValue}
                ref={getRef}
                css={css}
            >
                <View
                    top={topPosistion}
                    pt="0px"
                    ml="5px"
                    pr={rightMargin}
                    alignItems="center"
                    position="relative"
                >
                    {svgVariant !== 'trophy_icon' ? (
                        <span>
                            {' '}
                            <b> {subtext} </b> <strong>{text}</strong>{' '}
                        </span>
                    ) : (
                        <strong>
                            {' '}
                            {text}{' '}
                            <Link
                                variant="primaryLink"
                                href={url}
                                fontSize="14px"
                            >
                                {subtext}{' '}
                            </Link>
                        </strong>
                    )}
                </View>
            </ListItem>
        </GridColumn>
    );
};

export const InfoV2 = ({
    companyName,
    convincers,
    companyUrl,
    hideForMobile,
}) => {
    const topConvincersEls = useRef([]);
    const bottomConvincersEls = useRef([]);
    // these are best guess default values before it is actually calculated to minimize layout shift
    const [heights, setHeights] = useState([2, 1, 1, 2, 2, 1]);
    const [bottomHeights, setBottomeights] = useState([]);

    const windowResize = useCallback(() => {
        const listOfHeights = [];
        const listOfBottomHeights = [];
        requestAnimationFrame(() => {
            for (let i = 0; i < topConvincersEls.current.length; i += 1) {
                if (topConvincersEls.current[i]?.clientHeight > 95)
                    listOfHeights.push(4);
                else if (topConvincersEls.current[i]?.clientHeight > 70)
                    listOfHeights.push(3);
                else if (topConvincersEls.current[i]?.clientHeight > 50)
                    listOfHeights.push(2);
                else listOfHeights.push(1);
            }
            for (let i = 0; i < bottomConvincersEls.current.length; i += 1) {
                if (bottomConvincersEls.current[i]?.clientHeight > 70)
                    listOfBottomHeights.push(3);
                else if (bottomConvincersEls.current[i]?.clientHeight > 50)
                    listOfBottomHeights.push(2);
                else listOfBottomHeights.push(1);
            }
            setBottomeights(listOfBottomHeights);
            setHeights(listOfHeights);
        });
    }, [topConvincersEls]);

    useEffect(() => {
        windowResize();
        window.addEventListener('resize', windowResize, false);
        return () => window.removeEventListener('resize', windowResize);
    }, [windowResize]);
    useEffect(() => {
        windowResize();
    }, [windowResize]);
    const topConvincers = convincers
        .filter(
            (c) =>
                c?.v2?.layout === 'top' ||
                c?.v2?.layout === 'automated_convincers' ||
                (c?.v2?.layout === 'review' && c?.v2?.percentage > 50)
        )
        .map((c) => ({ ...c?.v2 }));

    const companyDefinedConvincers = convincers
        .filter((c) => c?.v2?.layout === 'company_defined')
        .map((c) => ({ ...c?.v2 }));
    if (!topConvincers.length && !companyDefinedConvincers.length) {
        return null;
    }
    const getSubtext = (info) => {
        if (info?.postText) return info.postText;
        if (info?.subtextv2 && info?.percentage) return `${info.percentage}%`;
        if (info?.subtextv2) return info.text;
        return '';
    };
    const getText = (info) => {
        if (info?.preText) return info.preText;
        if (info?.subtextv2) return info?.subtextv2;
        return info.text;
    };

    const reviewUrl = `https://fairygodboss.com/company-reviews/${companyUrl}`;
    const titleMargin = topConvincers.length
        ? { _: '0 0 28px 0', md: '0 0 32px 0' }
        : { _: '0 0 16px 0' };
    return (
        <View
            borderBottomStyle="solid"
            borderWidth="1px"
            borderColor={colors.secondary[20]}
            pt={{ _: '24px', md: '32px' }}
            pb="16px"
            css={{
                ...(hideForMobile
                    ? {
                          '@media screen and (max-width: 599px)': {
                              display: 'none',
                          },
                      }
                    : {}),
            }}
        >
            <Grid>
                <GridColumn size={{ md: 12 }}>
                    <View
                        as="h3"
                        fontFamily="Open Sans, sans-serif"
                        fontSize={{ _: '16px', md: '18px' }}
                        fontWeight={600}
                        lineHeight="24px"
                        margin={titleMargin}
                        color={colors.neutral[100]}
                    >
                        Why you should apply for a job to {companyName}:
                    </View>
                </GridColumn>
                <Grid
                    css={{
                        marginLeft: '0px',
                        marginRight: '0px',
                        marginBottom: '5px',
                        minHeight: '15px',
                        width: '100%',
                    }}
                >
                    {topConvincers.map((c, idx) => {
                        // eslint-disable-next-line no-return-assign
                        const getRef = (element) =>
                            (topConvincersEls.current[idx] = element);
                        return (
                            <ListItemSVG
                                key={`info-${c.text}`}
                                index={idx}
                                variant="icon"
                                svgVariant={c?.svg2 ? c.svg2 : `diversity`}
                                text={getText(c)}
                                url={c.url}
                                getRef={getRef}
                                numberOfLines={
                                    heights[idx] ? heights[idx] : false
                                }
                                subtext={getSubtext(c)}
                                size={
                                    topConvincers.length === 1
                                        ? { _: 12 }
                                        : { _: 12, md: 6 }
                                }
                                isLast={topConvincers.length === idx + 1}
                            />
                        );
                    })}
                </Grid>
                {topConvincers.length > 0 && (
                    <Grid
                        size={{ _: 12 }}
                        css={{
                            margin: '0px',
                        }}
                    >
                        <GridColumn
                            css={{
                                button: {
                                    left: '-6px',
                                    background: 'transparent',
                                },
                                'button:hover': {
                                    background: 'transparent',
                                },
                                'button:active': {
                                    background: 'transparent',
                                },
                                'button:focus': {
                                    background: 'transparent',
                                },
                                '@media screen and (max-width: 1056px)': {
                                    'div[data-qa="popover-text"]': {
                                        'margin-top': '12px',
                                    },
                                    button: {
                                        left: '-10px',
                                    },
                                },
                                '@media screen and (max-width: 905px)': {
                                    button: {
                                        width: '44px!important',
                                        height: '44px',
                                        'min-width': '44px',
                                        left: '-13px',
                                    },
                                },
                            }}
                            size={{ _: 1, md: 0.5 }}
                        >
                            <Popover
                                className="popover"
                                height={{ _: '44px', md: '32px' }}
                                color={colors.neutral[100]}
                                lineHeight="24px"
                                message="Your trust is our priority. Reviews cannot be altered, influenced, or removed by companies."
                            />
                        </GridColumn>
                        <GridColumn size={{ _: 11, md: 11.5 }}>
                            <View
                                fontSize="14px"
                                lineHeight="18px"
                                fontWeight="400"
                                color={colors.neutral[70]}
                                css={{
                                    '@media screen and (min-width: 436px) and (max-width: 905px)': {
                                        left: '-3%',
                                        position: 'relative',
                                    },
                                    'margin-top': '2px',
                                    '@media screen and (min-width: 906px)': {
                                        'margin-top': '1%',
                                    },
                                    a: {
                                        'text-decoration': 'none!important',
                                        'font-weight': '600',
                                    },
                                    button: {
                                        padding: '0px',
                                    },
                                }}
                            >
                                Ratings are based on anonymous reviews by
                                Fairygodboss members.{' '}
                                <a
                                    href={reviewUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <Button
                                        variant="primaryLink"
                                        href={reviewUrl}
                                        fontSize="14px"
                                        lineHeight="18px"
                                        rightIcon={
                                            <Icon
                                                pt="1px"
                                                variant="chevron_right_16"
                                                outlineColor={
                                                    colors.primary[60]
                                                }
                                            />
                                        }
                                    >
                                        {' '}
                                        See all reviews{' '}
                                    </Button>
                                </a>
                            </View>
                        </GridColumn>
                    </Grid>
                )}
                {companyDefinedConvincers.length > 0 && (
                    <GridColumn
                        size={{ md: 11.5 }}
                        marginTop={topConvincers.length > 0 ? '16px' : '-25px'}
                        css={{
                            background: '#F2ECFE99',
                            borderRadius: '12px',
                            padding: '24px',
                        }}
                    >
                        {companyDefinedConvincers.map((c, idx) => {
                            // eslint-disable-next-line no-return-assign
                            const getRef = (element) =>
                                (bottomConvincersEls.current[idx] = element);
                            return (
                                <ListItemSVG
                                    key={`info-${c.text}`}
                                    variant="icon"
                                    svgVariant="ribbon"
                                    text={
                                        c?.layout === 'automated_convincers'
                                            ? c?.subtextv2
                                            : c.text
                                    }
                                    getRef={getRef}
                                    numberOfLines={
                                        bottomHeights[idx]
                                            ? bottomHeights[idx]
                                            : false
                                    }
                                    size={{ _: 12, md: 12 }}
                                    isLast={
                                        companyDefinedConvincers.length ===
                                        idx + 1
                                    }
                                />
                            );
                        })}
                    </GridColumn>
                )}
            </Grid>
        </View>
    );
};

InfoV2.propTypes = {
    convincers: PropTypes.arrayOf(convincerPropType),
    companyName: PropTypes.string.isRequired,
    companyUrl: PropTypes.string.isRequired,
    hideForMobile: PropTypes.bool,
};

InfoV2.defaultProps = {
    convincers: [],
    hideForMobile: false,
};
